/* eslint-disable eqeqeq */
// @ts-nocheck
import { CFormInput, CFormSelect, CSpinner } from "@coreui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import UndoIcon from "../../../components/icons/UndoIcon";
import FileUploader from "../../../components/ui/FileUploader";
import { Field, FormSection } from "../../../components/ui/FormUi";
import InfiniteScrollSelect from "../../../components/ui/InfiniteScrollSelect";
import RichTextEditor from "../../../components/ui/RichTextEditor";
import { FormMode } from "../../../helpers/enums";
import { verifyFileList } from "../../../helpers/general";
import { useSelectedPlace } from "../../../hooks/place/useSelectedPlace";
import { useAuth } from "../../../hooks/useAuth";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import { cardStyle } from "../../Places/PlacesTable"; // adjust path

const packItemSchema = {
  title: yup.string().required("Ce champ est obligatoire"),
  isSaved: yup.boolean().default(false),
  isDeleted: yup.boolean().default(false),
};

const isPackItemsEmpty = (items) =>
  (items ?? []).filter((item) => item.isDeleted === false).length <= 0;

const isTaxiOn = (v) => v === "true" || v === true;

export default function NewPack({ mode = FormMode.CREATE }) {
  const [formLoading, setFormLoading] = useState(false);
  const [description, setDescription] = useState("");
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const navigate = useNavigate();
  const params = useParams();

  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const schema = yup.object({
    title: yup.string().required("Ce champ est obligatoire"),
    image: yup.mixed().required("Ce champ est obligatoire"),
    shortDescription: yup.string().nullable(),
    description: yup.string().nullable(),
    price: yup
      .number()
      .positive()
      .required("Ce champ est obligatoire")
      .typeError("S'il vous plait, entrez un nombre valide")
      .transform((v) => (isNaN(v) ? undefined : v)),
    priceInEuro: yup
      .number()
      .positive()
      .nullable(true)
      .typeError()
      .transform((_, val) => (val ? Number(val) : null)),
    ...(isAdmin
      ? {
          placeId: yup
            .object()
            .required("Ce champ est obligatoire")
            .typeError("Ce champ est obligatoire"),
        }
      : {}),
    items: yup
      .array()
      .min(1, "Veuillez saisir au moins un élément")
      .of(yup.object().shape(packItemSchema)),

    taxiActive: yup.string().optional(),
    latitude: yup.number().when("taxiActive", {
      is: isTaxiOn,
      then: () =>
        yup
          .number()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .number()
          .nullable()
          .optional()
          .typeError("Ce champ est obligatoire")
          .transform((_, val) => (val == Number(val) ? Number(val) : null)),
    }),
    longitude: yup.number().when("taxiActive", {
      is: isTaxiOn,
      then: () =>
        yup
          .number()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .number()
          .nullable(true)
          .optional()
          .typeError("Ce champ est obligatoire")
          .transform((_, val) => (val == Number(val) ? Number(val) : null)),
    }),
    addressName: yup.number().when("taxiActive", {
      is: isTaxiOn,
      then: () =>
        yup
          .string()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .string()
          .nullable(true)
          .optional()
          .typeError("Ce champ est obligatoire"),
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control: formControl,
    setValue: setFormValue,
    watch: watchFormValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      taxiActive: "false",
      items: [{ title: "", isSaved: false, isDeleted: false }],
    },
  });

  const selectedPlace = watchFormValue("placeId");
  const [image, setImage] = useState(1);
  const {
    fields: packItemsFields,
    append: appendPackItemField,
    remove: removePackItemField,
    update: updatePackItemField,
  } = useFieldArray({
    control: formControl,
    name: "items",
  });

  const formPackItemsValue = watchFormValue("items");
  const taxiActiveValue = watchFormValue("taxiActive");

  const createNewPackMutation = useMutation(async (data) => {
    return await apiService.MakePostRequest("packs", data, token, true);
  });

  const updatePackMutation = useMutation(async (data) => {
    return await apiService.MakePutRequest(
      `packs/${params.id}?placeId=${selectedPlaceId}`,
      data,
      token,
      true,
    );
  });

  const updatePackItemMutation = useMutation(async ({ packItemId, data }) => {
    return await apiService.MakePutRequest(
      `packs/${params.id}/pack-item/${packItemId}?placeId=${selectedPlaceId}`,
      data,
      token,
    );
  });

  const createNewPackItemMutation = useMutation(async ({ packId, data }) => {
    return await apiService.MakePostRequest(
      `packs/${packId}/pack-item?placeId=${selectedPlaceId}`,
      data,
      token,
    );
  });

  const deletePackItemMutation = useMutation(async (packItemId) => {
    return await apiService.MakeDeleteRequest(
      `packs/${params.id}/pack-item/${packItemId}?placeId=${selectedPlaceId}`,
      token,
    );
  });

  const createPack = async (data) => {
    try {
      setFormLoading(true);
      const newPack = await createNewPackMutation.mutateAsync(data);
      const packItems = data.items.map(async (item) => {
        return await createNewPackItemMutation.mutateAsync({
          packId: newPack.id,
          data: item,
        });
      });
      await Promise.all(packItems);
      toastSuccess(general.fr.message.packCreated);
      navigate(`/packs`);
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const updatePack = async (data) => {
    try {
      setFormLoading(true);
      const updatedPack = await updatePackMutation.mutateAsync(data);
      const packItemsToDelete = data.items.filter(
        (packItem) => packItem.isDeleted === true && packItem.isSaved === true,
      );
      const packItemsToUpdate = data.items.filter(
        (packItem) => packItem.isSaved === true && packItem.isDeleted === false,
      );
      const packItemsToCreate = data.items.filter(
        (packItem) => packItem.isSaved === false,
      );
      const deletedPackItems = packItemsToDelete.map(async (item) => {
        return await deletePackItemMutation.mutateAsync(item.itemId);
      });
      const updatedPackItems = packItemsToUpdate.map(async (item) => {
        return await updatePackItemMutation.mutateAsync({
          packItemId: item.itemId,
          data: item,
        });
      });
      const newPackItems = packItemsToCreate.map(async (item) => {
        return await createNewPackItemMutation.mutateAsync({
          packId: updatedPack.id,
          data: item,
        });
      });
      await Promise.all(deletedPackItems);
      await Promise.all(updatedPackItems);
      await Promise.all(newPackItems);
      toastSuccess(general.fr.message.packCreated);
      navigate(`/packs`);
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const onSubmit = async (dataArg) => {
    const data = { ...dataArg };
    try {
      if (isPackItemsEmpty(data.items)) return;
      const imageFile = verifyFileList(data.image);
      data.image = imageFile;
      data.description = description;
      data.placeId = isAdmin ? data.placeId.id : selectedPlaceId;
      if (mode === FormMode.UPDATE) {
        updatePack(data);
      } else {
        createPack(data);
      }
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  useEffect(() => {
    if (mode === FormMode.UPDATE && params?.id) {
      const formFields = Object.keys(schema.fields);
      apiService.MakeGetRequest(`packs/${params.id}`).then((response) => {
        setDescription(response?.description);
        setFormValue("placeId", response.place);
        setImage(response?.image);
        Object.entries(response).forEach((entry) => {
          if (formFields.indexOf(entry[0]) !== -1) {
            if (entry[0] === "items") {
              const formatedItems = entry[1].map((item) => ({
                ...item,
                itemId: item.id,
                isSaved: true,
                isDeleted: false,
              }));
              setFormValue("items", formatedItems);
            } else {
              setFormValue(entry[0], entry[1]);
            }
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deletePackItem = async (item, index) => {
    if (item.isSaved === true) {
      updatePackItemField(index, { ...item, isDeleted: true });
    } else {
      removePackItemField(index);
    }
  };

  const isCreate = mode !== FormMode.UPDATE;
  const taxiOn = isTaxiOn(taxiActiveValue);
  const itemsEmpty = isPackItemsEmpty(formPackItemsValue);
  const itemsCount = (formPackItemsValue ?? []).filter(
    (i) => !i.isDeleted,
  ).length;

  return (
    <div className="vz-form px-2 mt-4">
      {/* Header */}
      <div
        style={{ ...cardStyle, padding: "16px 20px" }}
        className="d-flex justify-content-between align-items-center mb-3"
      >
        <div>
          <h4 className="fs-4 fw-bold mb-0">
            {isCreate ? "Ajouter un pack" : "Mise à jour du pack"}
          </h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Les champs marqués d'une * sont obligatoires
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/packs")}
          className="btn btn-outline-secondary rounded-pill px-3"
        >
          ← Retour
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* General */}
        <FormSection title="Informations générales">
          <Field
            label="Titre du pack"
            htmlFor="title"
            required
            error={errors.title?.message}
          >
            <CFormInput
              id="title"
              placeholder="Titre du pack"
              {...register("title")}
            />
          </Field>

          {isAdmin && (
            <Field
              label={
                selectedPlace?.isVisible === false
                  ? "Établissement (cet établissement est invisible)"
                  : "Établissement"
              }
              htmlFor="placeId"
              required
              error={errors.placeId?.message}
            >
              <Controller
                name="placeId"
                control={formControl}
                render={({ field }) => (
                  <InfiniteScrollSelect
                    id="placeId"
                    isClearable={false}
                    getOptionLabel={(o) => o?.name}
                    getOptionValue={(o) => o.id}
                    isOptionSelected={(o, selected) =>
                      o?.id === selected?.[0]?.id
                    }
                    url={{ path: "places" }}
                    {...field}
                  />
                )}
              />
            </Field>
          )}

          <Field
            label="Image"
            htmlFor="image"
            required
            error={errors.image?.message}
          >
            <FileUploader
              id="image"
              name="image"
              accept="image/png,image/jpeg,image/jpg"
              required={isCreate}
              {...register("image")}
              file={image}
            />
          </Field>

          <Field
            label="Brève description"
            htmlFor="shortDescription"
            error={errors.shortDescription?.message}
          >
            <CFormInput
              id="shortDescription"
              placeholder="Brève description"
              {...register("shortDescription")}
            />
          </Field>

          <div className="col-12">
            <label
              className="d-block mb-1 fw-semibold"
              style={{ fontSize: 13 }}
            >
              Description
            </label>
            <RichTextEditor
              defaultValue={description}
              onChange={(data) => setDescription(data)}
            />
          </div>
        </FormSection>

        {/* Pricing */}
        <FormSection title="Tarification">
          <Field
            label="Prix (DH)"
            htmlFor="price"
            required
            error={errors.price?.message}
          >
            <CFormInput
              id="price"
              type="number"
              step="0.01"
              placeholder="Prix"
              {...register("price")}
            />
          </Field>
          <Field
            label="Prix en euro (€)"
            htmlFor="priceInEuro"
            error={errors.priceInEuro?.message}
          >
            <CFormInput
              id="priceInEuro"
              type="number"
              step="0.01"
              placeholder="Prix en euro"
              {...register("priceInEuro")}
            />
          </Field>
        </FormSection>

        {/* Taxi (admin) */}
        {isAdmin && (
          <FormSection title="Configuration taxi">
            <Field
              label="Taxi actif"
              htmlFor="taxiActive"
              required
              error={errors.taxiActive?.message}
            >
              <CFormSelect id="taxiActive" {...register("taxiActive")}>
                <option value="false">Non</option>
                <option value="true">Oui</option>
              </CFormSelect>
            </Field>
            <Field
              label="Adresse taxi"
              htmlFor="addressName"
              required={taxiOn}
              error={errors.addressName?.message}
            >
              <input
                id="addressName"
                className="form-control"
                placeholder="Adresse"
                {...register("addressName")}
              />
            </Field>
            <Field
              label="Latitude"
              htmlFor="latitude"
              required={taxiOn}
              error={errors.latitude?.message}
            >
              <input
                id="latitude"
                type="number"
                step="0.0000000001"
                className="form-control"
                {...register("latitude")}
              />
            </Field>
            <Field
              label="Longitude"
              htmlFor="longitude"
              required={taxiOn}
              error={errors.longitude?.message}
            >
              <input
                id="longitude"
                type="number"
                step="0.0000000001"
                className="form-control"
                {...register("longitude")}
              />
            </Field>
          </FormSection>
        )}

        {/* Pack items */}
        <section style={cardStyle} className="mb-3">
          <div
            className="d-flex justify-content-between align-items-center mb-3 pb-2"
            style={{ borderBottom: "1px solid #f0f0f0" }}
          >
            <div>
              <h6 className="fw-bold mb-0" style={{ fontSize: 15 }}>
                Éléments du pack {itemsCount > 0 && `(${itemsCount})`}
              </h6>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Listez ce que contient le pack
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary rounded-pill px-3"
              onClick={() =>
                appendPackItemField({
                  title: "",
                  isSaved: false,
                  isDeleted: false,
                })
              }
            >
              + Ajouter un élément
            </button>
          </div>

          {itemsEmpty && (
            <div
              className="text-center py-3"
              style={{
                border: "1px dashed #f1aeb5",
                borderRadius: 12,
                fontSize: 13,
                color: "#dc3545",
              }}
            >
              Le pack doit contenir au moins un élément
            </div>
          )}

          <div className="d-flex flex-column gap-3">
            {packItemsFields.map((packItem, index) => {
              const deleted = packItem?.isDeleted;
              return (
                <div
                  key={packItem?.id}
                  className="d-flex align-items-end gap-3 p-3"
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 12,
                    backgroundColor: "#fcfcfc",
                  }}
                >
                  <div
                    className="flex-grow-1 row g-3"
                    style={{
                      opacity: deleted ? 0.4 : 1,
                      pointerEvents: deleted ? "none" : "all",
                    }}
                  >
                    <Field
                      col="col-12"
                      label={`Élément ${index + 1}`}
                      htmlFor={`item_title_${index}`}
                      required
                      error={errors?.items?.[index]?.title?.message}
                    >
                      <CFormInput
                        id={`item_title_${index}`}
                        placeholder="Titre"
                        {...register(`items.${index}.title`)}
                      />
                    </Field>
                  </div>

                  {deleted ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill px-3 mb-1"
                      title="Annuler la suppression"
                      onClick={() =>
                        updatePackItemField(index, {
                          ...packItem,
                          isDeleted: false,
                        })
                      }
                    >
                      <UndoIcon />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger rounded-pill px-3 mb-1"
                      onClick={() => deletePackItem(packItem, index)}
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {errors.items?.message && (
            <div style={{ color: "#dc3545", fontSize: 12 }} className="mt-2">
              {errors.items.message}
            </div>
          )}
        </section>

        {/* Sticky actions */}
        <div
          className="d-flex justify-content-end gap-2 py-3 px-3 mt-3"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            backgroundColor: "#fff",
            borderRadius: 16,
            boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/packs")}
            className="btn btn-outline-secondary rounded-pill px-4"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={formLoading}
            className="btn btn-success text-white rounded-pill px-4"
          >
            {formLoading ? <CSpinner size="sm" /> : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
